import { use, useEffect, useState } from "react";
import AddInventory from "./AddInventory";
import {
  createOpeningInventory,
  deleteOpeningInventory,
  getOpeningInventoriesAPI,
  updateOpeningInventory,
} from "../../services/inventoryService";
import { confirmAction, errorAlert } from "../common/Swal";

// ─── Dummy data ───────────────────────────────────────────────────────────────
const DUMMY = [
  {
    _id: "1",
    type: "gold",
    createdAt: "2025-06-01T09:15:00Z",
    totalWeight: 105.32,
    categories: [
      {
        catKey: "pc",
        rows: [
          { brandName: "22K Hallmark", qty: 4, weight: 46.5, unit: "gram" },
          { brandName: "Dubai Gold", qty: 2, weight: 23.1, unit: "gram" },
        ],
      },
      {
        catKey: "bar",
        rows: [{ brandName: "PMEX Bar", qty: 1, weight: 35.72, unit: "gram" }],
      },
    ],
  },
  {
    _id: "2",
    type: "gold",
    createdAt: "2025-06-03T11:40:00Z",
    totalWeight: 58.664,
    categories: [
      { catKey: "lab", rows: [{ qty: 3, weight: 35.0, unit: "gram" }] },
      { catKey: "ginni", rows: [{ qty: 2, weight: 23.664, unit: "gram" }] },
    ],
  },
  {
    _id: "3",
    type: "silver",
    createdAt: "2025-06-05T14:20:00Z",
    totalWeight: 450.0,
    categories: [
      { catKey: "silver", rows: [{ qty: 10, weight: 450.0, unit: "gram" }] },
    ],
  },
  {
    _id: "4",
    type: "cash",
    createdAt: "2025-06-08T08:00:00Z",
    cashAmount: 750000,
    categories: [],
  },
  {
    _id: "5",
    type: "gold",
    createdAt: "2025-06-10T16:55:00Z",
    totalWeight: 233.28,
    categories: [
      {
        catKey: "coin",
        rows: [
          { brandName: "Jubilee Coin", qty: 10, weight: 116.64, unit: "gram" },
          { brandName: "Mughal Coin", qty: 10, weight: 116.64, unit: "gram" },
        ],
      },
    ],
  },
  {
    _id: "6",
    type: "cash",
    createdAt: "2025-06-11T10:10:00Z",
    cashAmount: 200000,
    categories: [],
  },
];

const CAT_LABELS = {
  pc: "PC",
  lab: "Lab",
  bar: "Bar",
  coin: "Coin",
  ginni: "Ginni",
  silver: "Silver",
};

const fmt = (d) =>
  new Date(d).toLocaleDateString("en-PK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const toGrams = (w, unit) =>
  (parseFloat(w) || 0) * (unit === "tola" ? 11.664 : 1);

function CategoryBadges({ categories }) {
  if (!categories?.length)
    return <span style={{ color: "#aaa", fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {categories.map(({ catKey, rows }) => {
        const g = rows.reduce((s, r) => s + toGrams(r.totalWeight, r.unit), 0);
        return (
          <span
            key={catKey}
            style={{
              fontSize: 11,
              background: "rgba(184,134,11,0.18)",
              color: "#b8860b",
              border: "1px solid rgba(184,134,11,0.35)",
              borderRadius: 6,
              padding: "2px 8px",
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            {CAT_LABELS[catKey] ?? catKey} · {g.toFixed(2)} g
          </span>
        );
      })}
    </div>
  );
}

// ─── Delete confirm ───────────────────────────────────────────────────────────
function DeleteConfirm({ item, onConfirm, onCancel }) {
  if (!item) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: "1.5rem",
          maxWidth: 380,
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <h6
          style={{
            fontWeight: 700,
            fontFamily: '"Inria Serif", serif',
            marginBottom: 6,
          }}
        >
          Delete inventory entry?
        </h6>
        <p style={{ fontSize: 13, color: "#555", marginBottom: "1rem" }}>
          This will permanently remove the{" "}
          <strong style={{ textTransform: "capitalize" }}>{item.type}</strong>{" "}
          entry dated <strong>{fmt(item.createdAt)}</strong>. This cannot be
          undone.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => onConfirm(item._id)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function InventoryList() {
  const [records, setRecords] = useState([]);
  const [activeType, setActiveType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Fetch opneing inventories:
    getOpeningInventories();
  }, []);

  async function getOpeningInventories() {
    try {
      setIsLoading(true);
      const response = await getOpeningInventoriesAPI();
      if (response?.success) {
        setRecords(response?.inventories || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSave = async (payload) => {
    setIsLoading(true);
    try {
      console.log({ payload });
      const response = await createOpeningInventory(payload);
      console.log({ response });
      if (response?.success) {
        await getOpeningInventories();
        return true;
      }
      throw new Error("Couldn't save opening inventory.");
    } catch (err) {
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  const handleUpdate = async (id, payload) => {
    setIsLoading(true);
    try {
      const response = await updateOpeningInventory(id, payload);
      if (response?.success) {
        await getOpeningInventories();
        return true;
      }

      throw new Error("Couldn't update inventory.");
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't update inventory.";
      errorAlert(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id) => {
    const confirmDelete = await confirmAction(
      "This will permanently remove the entry. This cannot be undone",
    );

    if (confirmDelete) {
      try {
        setIsLoading(true);
        const response = await deleteOpeningInventory(id);
        if (response?.success) {
          await getOpeningInventories();
          return true;
        } else {
          throw new Error(response?.message || "Couldn't delete inventory.");
        }
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Couldn't delete inventory.";
        errorAlert(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const openAdd = () => {
    setEditData(null);
    setShowModal(true);
  };
  const openEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const filtered =
    activeType === "all"
      ? records
      : records.filter((r) => r.type === activeType);

  const totalGold = records
    .filter((r) => r.type === "gold")
    .reduce((s, r) => s + (r.totalWeight ?? 0), 0);
  const totalSilver = records
    .filter((r) => r.type === "silver")
    .reduce((s, r) => s + (r.totalWeight ?? 0), 0);
  const totalCash = records
    .filter((r) => r.type === "cash")
    .reduce((s, r) => s + (r.cashAmount ?? 0), 0);

  const categories = [
    {
      key: "gold",
      label: "Gold",
      style: { background: "#ffffff", border: "2px solid #ddd" },
      iconColor: "#b8860b",
      textColor: "#b8860b",
      stat: `${totalGold.toFixed(2)} g`,
    },
    {
      key: "silver",
      label: "Silver",
      style: { background: "#b8860b" },
      iconColor: "#fff",
      textColor: "#fff",
      stat: `${totalSilver.toFixed(2)} g`,
    },
    {
      key: "cash",
      label: "Cash",
      style: { background: "#505050" },
      iconColor: "#fff",
      textColor: "#fff",
      stat: `Rs. ${totalCash.toLocaleString("en-PK")}`,
    },
    {
      key: "all",
      label: "All",
      style: { background: "#4472c4" },
      iconColor: "#fff",
      textColor: "#fff",
      stat: `${records.length} entries`,
    },
  ];

  const icons = {
    gold: "fa-coins",
    silver: "fa-layer-group",
    cash: "fa-money-bill-wave",
    all: "fa-boxes-stacked",
  };

  return (
    <div className="inventory-container p-4">
      {/* ── Category boxes ── */}
      <div className="row mb-2">
        {categories.map(({ key, label, style, iconColor, textColor, stat }) => (
          <div className="col-6 col-md-3" key={key}>
            <div
              className={`category-box${activeType === key ? " active" : ""}`}
              style={style}
              onClick={() => setActiveType(key)}
            >
              <div className="category-content">
                <i
                  className={`fa-solid ${icons[key]}`}
                  style={{ fontSize: 32, color: iconColor }}
                />
                <h3
                  style={{
                    color: textColor,
                    fontFamily: '"Inria Serif", serif',
                    fontSize: 20,
                    margin: 0,
                    fontWeight: 700,
                  }}
                >
                  {label}
                </h3>
                <span
                  style={{
                    color: textColor,
                    fontSize: 12,
                    opacity: 0.85,
                    fontFamily: '"Inria Serif", serif',
                  }}
                >
                  {stat}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="filter-bar mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h5
          style={{
            margin: 0,
            fontFamily: '"Inria Serif", serif',
            fontWeight: 700,
            color: "#333",
            fontSize: 18,
          }}
        >
          Inventory
          <span
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: "#888",
              marginLeft: 10,
            }}
          >
            {filtered.length} {activeType === "all" ? "total" : activeType}{" "}
            entries
          </span>
        </h5>
        <button className="btn-apply" onClick={openAdd}>
          + Add Inventory
        </button>
      </div>

      {/* ── Table ── */}
      {filtered.length === 0 ? (
        <div
          className="table-wrapper p-5 text-center"
          style={{
            color: "#888",
            fontFamily: '"Inria Serif", serif',
            fontSize: 15,
          }}
        >
          No inventory entries found.
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="product-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Type</th>
                <th>Categories</th>
                <th style={{ textAlign: "right" }}>Weight / Amount</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => (
                <tr key={item._id}>
                  <td style={{ color: "#ccc", fontWeight: 500 }}>{idx + 1}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {fmt(item.createdAt)}
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        background:
                          item.type === "gold"
                            ? "rgba(184,134,11,0.2)"
                            : item.type === "silver"
                              ? "rgba(255,255,255,0.15)"
                              : "rgba(255,255,255,0.1)",
                        color: item.type === "gold" ? "#f0c040" : "#fff",
                        border: `1px solid ${item.type === "gold" ? "rgba(184,134,11,0.5)" : "rgba(255,255,255,0.2)"}`,
                        borderRadius: 5,
                        padding: "2px 9px",
                      }}
                    >
                      {item.type}
                    </span>
                  </td>
                  <td>
                    {item.type === "cash" ? (
                      <span style={{ color: "#bbb", fontSize: 12 }}>
                        Cash deposit
                      </span>
                    ) : (
                      <CategoryBadges categories={item.categories} />
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                    }}
                  >
                    {item.type === "cash" ? (
                      <span style={{ color: "#6ee7b7" }}>
                        Rs. {(item.cashAmount ?? 0).toLocaleString("en-PK")}
                      </span>
                    ) : (
                      <span style={{ color: "#fcd34d" }}>
                        {(item.totalWeight ?? 0).toFixed(3)} g
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => openEdit(item)}
                        style={{
                          fontSize: 12,
                          padding: "4px 12px",
                          cursor: "pointer",
                          fontFamily: '"Inria Serif", serif',
                          background: "transparent",
                          color: "#93c5fd",
                          border: "1px solid #93c5fd",
                          borderRadius: 4,
                          fontWeight: 600,
                          transition: "0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#93c5fd";
                          e.target.style.color = "#1e3a5f";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.color = "#93c5fd";
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item?._id)}
                        style={{
                          fontSize: 12,
                          padding: "4px 12px",
                          cursor: "pointer",
                          fontFamily: '"Inria Serif", serif',
                          background: "transparent",
                          color: "#fca5a5",
                          border: "1px solid #fca5a5",
                          borderRadius: 4,
                          fontWeight: 600,
                          transition: "0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#fca5a5";
                          e.target.style.color = "#7f1d1d";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.color = "#fca5a5";
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      <AddInventory
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditData(null);
        }}
        onSave={handleSave}
        onUpdate={handleUpdate}
        isLoading={isLoading}
        editData={editData}
      />

      {/* <DeleteConfirm
        item={deleteTarget}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      /> */}
    </div>
  );
}
